import { useState, useCallback, useRef } from 'react';
import { useUserStore } from '~providers/userStore/zustand';
import FirebaseChatsServiceWeb from '~services/firebase-web/FirebaseChatsServiceWeb';
import { ChatTypeWithKey } from '~types/chat';

// Support user IDs that can receive support chats
const SUPPORT_USER_IDS = [
  'bxcOenW6joNFwgiMBctR0zJT07F2', // Pyxi Support (Primary)
  'wLJLEn8J6oN9RpPyep2BjdnagcA2', // Pyxi Business/Host
];

const SUPPORT_USER_ID = 'bxcOenW6joNFwgiMBctR0zJT07F2'; // Default primary support
const SUPPORT_USER_NAME = 'Pyxi Support';

interface UseSupportChatResult {
  contactChat: ChatTypeWithKey | null;
  isLoading: boolean;
  error: string | null;
  createOrFetchSupportChat: () => Promise<ChatTypeWithKey | null>;
}

/**
 * Hook to manage support chat creation and retrieval
 * Creates a 'contact-pyxi' type chat with the support user if it doesn't exist
 */
export const useContactSupportChat = (): UseSupportChatResult => {
  const user = useUserStore(state => state.user);
  const uid = user?.uid;

  const [contactChat, setContactChat] = useState<ChatTypeWithKey | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOrFetchSupportChat = useCallback(async (): Promise<ChatTypeWithKey | null> => {
    try {
      if (!uid) {
        throw new Error('User not authenticated');
      }

      setIsLoading(true);
      setError(null);

      console.log('🔄 [SUPPORT-CHAT] Attempting to create/fetch support chat for user:', uid);

      // Subscribe to user chats once
      return new Promise((resolve, reject) => {
        const unsubscribe = FirebaseChatsServiceWeb.subscribeToUserChats(
          uid,
          (chats: ChatTypeWithKey[]) => {
            console.log('📊 [SUPPORT-CHAT] Received', chats.length, 'chats');
            
            const existingSupportChat = chats.find(
              (chat: any) =>
                chat.type === 'contact-pyxi' &&
                chat.userIds?.includes(uid) &&
                SUPPORT_USER_IDS.some(supportId => chat.userIds?.includes(supportId))
            );

            if (existingSupportChat) {
              console.log('✅ [SUPPORT-CHAT] Found existing support chat:', existingSupportChat.key);
              setContactChat(existingSupportChat);
              setIsLoading(false);
              unsubscribe();
              resolve(existingSupportChat);
              return;
            }

            // If no existing chat found, create one
            (async () => {
              try {
                console.log('📝 [SUPPORT-CHAT] Creating new support chat...');

                const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User';

                await FirebaseChatsServiceWeb.createContactUsChat({
                  user_id1: uid,
                  user_id2: SUPPORT_USER_ID,
                  user_name1: userName,
                  user_name2: SUPPORT_USER_NAME,
                });

                console.log('✅ [SUPPORT-CHAT] Support chat created, waiting for sync...');

                // Wait for the new chat to appear
                let attempts = 0;
                const maxAttempts = 15; // 15 attempts × 500ms = 7.5 seconds timeout

                const checkForNewChat = () => {
                  attempts++;
                  
                  // Re-subscribe to get fresh data
                  const checkUnsubscribe = FirebaseChatsServiceWeb.subscribeToUserChats(
                    uid,
                    (updatedChats: ChatTypeWithKey[]) => {
                      const newSupportChat = updatedChats.find(
                        (chat: any) =>
                          chat.type === 'contact-pyxi' &&
                          chat.userIds?.includes(uid) &&
                          SUPPORT_USER_IDS.some(supportId => chat.userIds?.includes(supportId))
                      );

                      if (newSupportChat) {
                        console.log('✅ [SUPPORT-CHAT] New support chat found and synced:', newSupportChat.key);
                        setContactChat(newSupportChat);
                        setIsLoading(false);
                        unsubscribe();
                        checkUnsubscribe();
                        resolve(newSupportChat);
                      } else if (attempts >= maxAttempts) {
                        console.error('❌ [SUPPORT-CHAT] Timeout: New chat not found after creation');
                        setError('Support chat created but could not be loaded');
                        setIsLoading(false);
                        unsubscribe();
                        checkUnsubscribe();
                        reject(new Error('Support chat not found after creation'));
                      } else {
                        checkUnsubscribe();
                        setTimeout(checkForNewChat, 500);
                      }
                    },
                    (err: any) => {
                      console.error('❌ [SUPPORT-CHAT] Error checking for new chat:', err);
                      checkUnsubscribe();
                      if (attempts < maxAttempts) {
                        setTimeout(checkForNewChat, 500);
                      }
                    }
                  );
                };

                setTimeout(checkForNewChat, 500);
              } catch (err: any) {
                console.error('❌ [SUPPORT-CHAT] Error creating chat:', err);
                setError(err?.message || 'Failed to create support chat');
                setIsLoading(false);
                unsubscribe();
                reject(err);
              }
            })();
          },
          (err: any) => {
            console.error('❌ [SUPPORT-CHAT] Error fetching chats:', err);
            setError(err?.message || 'Failed to load chats');
            setIsLoading(false);
            reject(err);
          }
        );

        // Timeout after 10 seconds
        const timeoutId = setTimeout(() => {
          console.error('❌ [SUPPORT-CHAT] Operation timeout');
          setError('Request timed out');
          setIsLoading(false);
          unsubscribe();
          reject(new Error('Support chat operation timed out'));
        }, 10000);

        // Store for cleanup
        (createOrFetchSupportChat as any).__cleanup = () => {
          clearTimeout(timeoutId);
          unsubscribe();
        };
      });
    } catch (err: any) {
      console.error('❌ [SUPPORT-CHAT] Error:', err);
      const errorMessage = err?.message || 'Failed to create support chat';
      setError(errorMessage);
      setIsLoading(false);
      return null;
    }
  }, [uid, user?.first_name, user?.last_name]);

  return {
    contactChat,
    isLoading,
    error,
    createOrFetchSupportChat,
  };
};
